import React, { useCallback, useState } from 'react';
import { useSelected, useFocused } from 'slate-react';
import { BarChart3, Settings, X } from 'lucide-react';

// Import chart components from existing Charts folder
import RiskByDepartment from '../../../../Charts/General/RiskByDepartment';
import RiskLevelByDepartment from '../../../../Charts/General/RiskLevelByDepartment';
import RiskLineChart from '../../../../Charts/General/RiskLineChart';
import RiskAppetite from '../../../../Charts/General/RiskAppetite';
import RiskSummary from '../../../../Charts/Department/RiskSummary';
import RiskSummaryReport from '../../../../Charts/Department/RiskSummary_Report';
import TopRisks from '../../../../Charts/Department/TopRisks';
import ActionStatus from '../../../../Charts/Department/ActionStatus';

// Available chart types
export const CHART_TYPES = {
  RISK_BY_DEPARTMENT: 'RiskByDepartment',
  RISK_LEVEL_BY_DEPARTMENT: 'RiskLevelByDepartment',
  RISK_LINE_CHART: 'RiskLineChart',
  RISK_APPETITE: 'RiskAppetite',
  RISK_SUMMARY: 'RiskSummary',
  RISK_SUMMARY_REPORT: 'RiskSummaryReport',
  TOP_RISKS: 'TopRisks',
  ACTION_STATUS: 'ActionStatus',
};

// Chart component mapping
const CHART_COMPONENTS = {
  [CHART_TYPES.RISK_BY_DEPARTMENT]: RiskByDepartment,
  [CHART_TYPES.RISK_LEVEL_BY_DEPARTMENT]: RiskLevelByDepartment,
  [CHART_TYPES.RISK_LINE_CHART]: RiskLineChart,
  [CHART_TYPES.RISK_APPETITE]: RiskAppetite,
  [CHART_TYPES.RISK_SUMMARY]: RiskSummary,
  [CHART_TYPES.RISK_SUMMARY_REPORT]: RiskSummaryReport,
  [CHART_TYPES.TOP_RISKS]: TopRisks,
  [CHART_TYPES.ACTION_STATUS]: ActionStatus,
};

// Chart labels for the selector
export const CHART_LABELS = {
  [CHART_TYPES.RISK_BY_DEPARTMENT]: 'Risk by Department',
  [CHART_TYPES.RISK_LEVEL_BY_DEPARTMENT]: 'Risk Level by Department',
  [CHART_TYPES.RISK_LINE_CHART]: 'Risk Line Chart',
  [CHART_TYPES.RISK_APPETITE]: 'Risk Appetite',
  [CHART_TYPES.RISK_SUMMARY]: 'Risk Summary',
  [CHART_TYPES.RISK_SUMMARY_REPORT]: 'Risk Summary Report',
  [CHART_TYPES.TOP_RISKS]: 'Top Risks',
  [CHART_TYPES.ACTION_STATUS]: 'Action Status',
};

function ChartElement({ attributes, children, element, chartData, onUpdate, onDelete }) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);

  const chartType = element.chartType;
  const ChartComponent = chartType ? CHART_COMPONENTS[chartType] : null;
  const chartHeight = element.height || 300;

  // Get the appropriate data for the chart type
  const getChartData = useCallback(() => {
    if (!chartData) return null;

    switch (chartType) {
      case CHART_TYPES.RISK_BY_DEPARTMENT:
        return { risks_by_department: chartData.risks_by_department };
      case CHART_TYPES.RISK_LEVEL_BY_DEPARTMENT:
        return { data: chartData.risk_level_by_department };
      case CHART_TYPES.RISK_LINE_CHART:
        return { data: chartData.risk_assessment_history };
      case CHART_TYPES.RISK_APPETITE:
        return { risk_appetite: chartData.risk_appetite };
      case CHART_TYPES.RISK_SUMMARY:
        return { data: chartData.risk_summary };
      case CHART_TYPES.RISK_SUMMARY_REPORT:
        return { 
          data: chartData.summary_for_report,
          type: element.reportType || 'financial',
        };
      case CHART_TYPES.TOP_RISKS:
        return { data: chartData.top_5_risks };
      case CHART_TYPES.ACTION_STATUS:
        return { data: chartData.action_status };
      default:
        return null;
    }
  }, [chartType, chartData, element.reportType]);

  const handleChartTypeChange = useCallback((type) => {
    if (onUpdate) {
      onUpdate(element.id, { chartType: type });
    }
  }, [element.id, onUpdate]);

  const handleHeightChange = useCallback((height) => {
    if (onUpdate) {
      onUpdate(element.id, { height: parseInt(height) });
    }
  }, [element.id, onUpdate]);

  const handleTitleChange = useCallback((title) => {
    if (onUpdate) {
      onUpdate(element.id, { title });
    }
  }, [element.id, onUpdate]);

  const handleReportTypeChange = useCallback((reportType) => {
    if (onUpdate) {
      onUpdate(element.id, { reportType });
    }
  }, [element.id, onUpdate]);

  // Handle button clicks - prevent Slate from capturing
  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(!showSettings);
  }, [showSettings]);

  const handleDeleteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id);
    }
  }, [element.id, onDelete]);

  const handleCloseSettings = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(false);
  }, []);

  return (
    <div 
      {...attributes}
      contentEditable={false}
      className={`chart-element relative my-4 rounded-lg border-2 transition-colors ${
        selected && focused ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-gray-300'
      }`}
    >
      {children}
      
      {/* Controls - always visible on hover or when selected */}
      <div 
        className={`absolute top-2 right-2 flex items-center gap-2 z-10 transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0 hover:opacity-100'
        }`}
        contentEditable={false}
      >
        <button
          type="button"
          onMouseDown={handleSettingsClick}
          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
          title="Settings"
        >
          <Settings size={16} />
        </button>
        {onDelete && (
          <button
            type="button"
            onMouseDown={handleDeleteClick}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-red-100 cursor-pointer"
            title="Delete"
          >
            <X size={16} className="text-red-500" />
          </button>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div 
          className="absolute top-12 right-2 bg-white rounded-lg shadow-xl border p-4 z-20 min-w-[280px]"
          contentEditable={false}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <h4 className="font-semibold mb-3">Chart Settings</h4>
          
          {/* Chart Type */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
            <select
              value={chartType || ''}
              onChange={(e) => handleChartTypeChange(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a chart...</option>
              {Object.entries(CHART_LABELS).map(([type, label]) => (
                <option key={type} value={type}>{label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={element.title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Chart title"
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Height */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Height (px)</label>
            <input
              type="number"
              value={chartHeight}
              onChange={(e) => handleHeightChange(e.target.value)}
              min="100"
              max="800"
              step="50"
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Report Type (for RiskSummaryReport) */}
          {chartType === CHART_TYPES.RISK_SUMMARY_REPORT && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
              <select
                value={element.reportType || 'financial'}
                onChange={(e) => handleReportTypeChange(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="financial">Financial</option>
                <option value="operational">Operational</option>
                <option value="reputational">Reputational</option>
              </select>
            </div>
          )}

          <button
            type="button"
            onMouseDown={handleCloseSettings}
            className="w-full mt-2 px-3 py-2 bg-gray-100 rounded-md text-sm hover:bg-gray-200 cursor-pointer"
          >
            Close
          </button>
        </div>
      )}

      {/* Chart Title */}
      {element.title && (
        <h3 className="text-lg font-semibold text-gray-800 mb-2 px-4 pt-4">
          {element.title}
        </h3>
      )}

      {/* Chart Display */}
      {ChartComponent && chartData ? (
        <div 
          className="chart-container p-4"
          style={{ height: chartHeight }}
        >
          <ChartComponent
            height={chartHeight - 32}
            {...getChartData()}
          />
        </div>
      ) : (
        /* Chart Placeholder */
        <div className="chart-placeholder border-2 border-dashed border-gray-300 rounded-lg p-8 m-4 text-center">
          <div className="flex flex-col items-center gap-4">
            <BarChart3 size={48} className="text-gray-400" />
            <p className="text-gray-500">Select a chart type to display</p>
            
            {/* Quick Chart Type Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              {Object.entries(CHART_LABELS).map(([type, label]) => (
                <button
                  key={type}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChartTypeChange(type);
                  }}
                  className="px-3 py-2 text-sm border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChartElement;
